// Mail-related root state — merged into the workspace shell via sharedLibraries.
//
// Every key the v1 client writes is declared HERE — and NOT only where it is
// written — because this slice is seeded at boot via the mail eager slice
// (workspace/sharedLibraries.js), and v3.14 root.update() on an undeclared
// key trips the schema check. The shapes follow architecture/MAIL.md §4.2.

export default {
  mailAccounts: [],
  mailActiveAccountId: null,
  mailActiveFolder: 'inbox',
  mailFolders: {},
  mailThreads: {},
  mailActiveThreadId: null,
  mailMessages: {},
  mailBodies: {},
  mailUnreadCounts: {},
  mailSelection: [],
  mailFilters: { unread: false, starred: false, attachments: false, from: null, label: null },
  mailSearchQuery: '',
  mailComposeOpen: false,
  mailComposeDraft: null,
  mailOutbox: [],

  // ── The read path (architecture/MAIL.md §3.1, MAIL-UI-READ-1) ──
  // GET /threads/:id in flight for this thread id (the view pane's loading
  // state), and the last thread open the server refused.
  mailThreadLoading: null,
  mailThreadError: null,
  // The list keyboard cursor (`j`/`k`); an id from the ACTIVE list slot.
  _mailCursorId: null,
  // Per-thread expansion map: { [threadId]: { [messageId]: true } } — unread
  // + newest open by default (loadThread.js seeds it).
  _mailMessageOpen: {},
  // Per-message quoted-text unfold: { [messageId]: true }.
  _mailQuotedOpen: {},

  // ── Compose lifecycle (architecture/MAIL.md §3.2, MAIL-UI-COMPOSE-1) ──
  // The 2 s autosave is in flight (the dock's "Saving…" hint).
  mailComposeSaving: false,
  // Epoch ms of the last landed autosave (the dock's "Saved" hint).
  mailComposeSavedAt: null,
  // `{ op: 'save'|'send', code, message }` — the last refused write. A send
  // failure keeps the dock open with the draft intact (§3.2 "the draft
  // reopens with the error").
  mailComposeError: null,
  // 'send' while POST /mail/send is in flight — disables the Send button.
  mailComposeBusy: null,
  // Recipient autocomplete: `{ field: 'to'|'cc'|'bcc', query }` while a
  // suggestion menu is open; null = closed.
  mailRecipientPicker: null,
  // Cc/Bcc rows are folded until asked for (the Gmail idiom).
  _mailComposeCcOpen: false,
  // Which dock menu is open — 'schedule' | 'from' | null.
  _mailComposeMenu: null,

  // ── The setup gate (architecture/MAIL.md §3.2a) ──
  // The whole `sdk.execute('mail.setup', 'get')` payload, plus a `__fetchedAt`
  // stamp the loader reads for its freshness window. Shape:
  //   { state, policy, viewerIsAdmin, accounts, providers, workspace }
  // `state` is one of off | ready | reauth | connect | link | ask-admin.
  // `providers[].steps` is served ONLY to admins — an EMPTY steps list on a
  // member is the contract, never an error.
  mailSetup: null,
  // The `get` is in flight. Three surfaces mount MailSetupPanel (the /mail
  // empty state, the header drawer, the /settings card), so the flag is what
  // keeps them from each firing their own request.
  mailSetupLoading: false,
  // `{ code, message }` when the `get` itself failed — the panel renders a
  // retry, never a blank box.
  mailSetupError: null,
  // The result of an ACTION (link / notifyAdmin / a provider button), as
  // `{ tone: 'info'|'success'|'error', key, message }`. `key` is a
  // `mail.setup.*` bundle key; `message` is a server string (the 501 contract
  // message, a 429 body) rendered verbatim when present.
  mailSetupNotice: null,
  // Which setup action is in flight — 'link' | 'notifyAdmin' | 'connect'.
  mailSetupBusy: null,

  // ── /settings Mail card (§3.4) ──
  // Per-account unsaved edits, `{ [accountId]: { signature, defaultSendAs } }`.
  mailSettingsDraft: {},
  // `{ id, op }` while a PATCH / DELETE is in flight on one account row.
  mailSettingsBusy: null,
  // `{ id, message }` — the last account write that the server refused.
  mailSettingsError: null,

  // ── /admin/mail (§3.5, MAIL-ADMIN-TENANT-POLICY-1) ──
  // Health rows (serializeAdminAccount shape) + the newest mail.* audit rows
  // (serializeAudit shape). `null` = never loaded; `[]` = loaded and empty —
  // the cards branch on that difference for their empty states.
  mailAdminAccounts: null,
  mailAdminAccountsLoading: false,
  mailAdminAccountsError: null,
  mailAdminAudit: null,
  mailAdminAuditLoading: false,
  mailAdminAuditError: null,
  // '<kind>:<op>' while a tenant action runs, 'disconnect:<id>' during a
  // force disconnect — one act at a time, buttons disable off it.
  mailAdminBusy: null,
  // The §6 policy key a save is in flight for — its row's control disables.
  mailPolicySaving: null,

  // ── Deep integrations (§3.7, MAIL-INTEGRATIONS-1) ──
  // Which pane the thread integrations dropdown shows — 'actions' (create
  // ticket / add to calendar / share) or 'share' (the channel picker). The
  // dropdown layer owns open/close; this only routes its content.
  _mailLinkMenuView: 'actions',
  // The share picker's channel filter query.
  _mailShareQuery: '',
  // One integration act at a time — 'ticket' | 'calendar' | 'share:<chId>' |
  // 'rsvp:<messageId>' | 'save:<attachmentId>' | null; buttons guard off it.
  _mailIntegrationBusy: null,

  // Phone rail (the MobileRailSheet presentation, P1) open flag.
  _mailRailOpen: false,
  // Docked MailOverlay open flag — the header envelope flips it on ANY route
  // (before the /mail route ever folds the context), so it must ride the
  // eager slice exactly like chat's `_chatDrawerOpen`.
  _mailDrawerOpen: false,
  // Subscription handle, workspace-keyed like root._chatRealtimeChannel.
  _mailStream: null
}
